import React, { useState, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { useLoadLeagueDataQuickView } from '../../../app/dataLoaderHooks';
import "../component.css";
import "./style.css";
import { League, LeaguePhase } from '../../../sdk/model';
import { 
  Container, 
  Typography, 
  Grid, 
  Button,
  Box,
  Card,
  CardContent,
  CardActions,
  Avatar,
  useTheme,
  useMediaQuery,
  Skeleton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  SportsFootball as SportsFootballIcon,
  Group as GroupIcon,
  EmojiEvents as TrophyIcon,
  CalendarMonth as ScheduleIcon,
  Info as InfoIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Sports as SportsIcon,
  Paid as AuctionIcon,
  Close
} from '@mui/icons-material';
import { useAppSelector } from '../../../app/hooks';
import { styled } from '@mui/material/styles';
import { Team } from '../../../sdk/model/team';
import { auctionApi } from '../../../adapters/APIExporter';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
  '& .MuiCardContent-root': {
    flexGrow: 1,
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  boxShadow: theme.shadows[2],
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[8],
  },
}));

interface LeagueWithMyTeam extends League {
  myTeam?: Team | undefined;
}

interface LeagueItemsProps {
  leagues: LeagueWithMyTeam[];
  isLoadComplete: boolean;
  isException: boolean;
  user: any;
  isMobile: boolean;
}

// Component for rendering league items
const LeagueItems = ({ leagues = [], isLoadComplete, isException, user, isMobile }: LeagueItemsProps) => {
  const navigate = useNavigate();
  
  if (isException) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error">Error loading leagues. Please try again later.</Typography>
      </Box>
    );
  }


  if (!isLoadComplete) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (leagues.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No leagues found
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/createLeague')}
          startIcon={<AddIcon />}
        >
          Create Your First League
        </Button>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {leagues.map((league) => (
        <Grid item xs={12} sm={6} md={4} key={league.id}>
          <StyledCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  {league.leagueName?.charAt(0) || 'L'}
                </Avatar>
                <Typography variant="h6" component="h3" noWrap>
                  {league.leagueName || 'Unnamed League'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <GroupIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {league.teams?.length || 0} Teams
                </Typography>
              </Box>
              
              {league.myTeam && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SportsFootballIcon color="primary" sx={{ mr: 1, fontSize: '1rem' }} />
                  <Typography variant="body2" color="primary">
                    Your Team: {league.myTeam.name}
                  </Typography>
                </Box>
              )}
              
              <Typography variant="caption" color="text.secondary">
                {league.phase} • {league.currentSeasonYear}
              </Typography>
            </CardContent>
            
            <CardActions sx={{ 
              p: 2, 
              pt: 0,
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: 1,
              width: '90%'
            }}>
              <Button 
                size="small" 
                color="primary"
                variant="outlined"
                onClick={() => navigate(`/league/${league.id}`)}
              >
                View League
              </Button>
              {league.phase === LeaguePhase.RegularSeason && league.myTeam && (
                <Button 
                  size="small" 
                  color="primary"
                  variant="outlined"
                  startIcon={<SportsIcon />}
                  onClick={() => navigate(`/league/${league.id}/matchup`)}
                >
                  Current Match
                </Button>
              )}
              {league.myTeam!! && <Button 
                size="small" 
                color="primary"
                variant="outlined"
                onClick={() => navigate(`/league/${league.id}/team/${league.myTeam?.id}`)}
              >
                View Team
              </Button>}
            </CardActions>
            
          </StyledCard>
        </Grid>
      ))}
    </Grid>
  );
};

// Main Home Component
const Home = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const user = useAppSelector((state) => state.user);
  const [isLoadComplete, setLoadComplete] = useState(false);
  const [isException, setIsException] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [creatingMock, setCreatingMock] = useState<string | undefined>(undefined);
  const [numberOfTeams, setNumberOfTeams] = useState<number | undefined>(undefined);

  const handleExpandClick = useCallback((panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  // Load league data
  const leaguesData = useLoadLeagueDataQuickView({
    userId: user?.id,
    loadRoster: false,
    setLoadComplete,
    setIsException
  }) || [];

  // Map the API response to our local League interface with proper type safety
  const leagues: LeagueWithMyTeam[] = (Array.isArray(leaguesData) ? leaguesData : [])
    .map(league => {
      let myTeam: Team | undefined;
      // Find user's team in this league
      
      if (user?.id && league.teams) {
        myTeam = league.teams.find(team => 
          team.owners?.some(owner => owner.id === user.id)
        ) || undefined;
      }
      
      return { ...league, myTeam };
    });

  function createMockAuction() {
    auctionApi.createMock(numberOfTeams).then(response => navigate('/auction/' + response.data));
  }

  // Featured content with collapsible sections
  const featuredContent = [
    {
      id: 'getting-started',
      icon: <InfoIcon color="primary" fontSize="large" />,
      title: 'Getting Started',
      description: 'New to CAPSized? Learn how to create or join a league and start managing your team today!',
      action: 'Learn More',
      onClick: () => navigate('/getting_started'),
    },
    {
      id: 'upcoming-deadlines',
      icon: <ScheduleIcon color="primary" fontSize="large" />,
      title: 'Upcoming Deadlines',
      description: 'Stay on top of important league dates and deadlines for trades, drafts, and more.',
      action: 'View Calendar',
      onClick: () => {},
    },
    {
      id: 'mock-auction',
      icon: <AuctionIcon color="primary" fontSize="large" />,
      title: 'Mock Auction',
      description: 'Practice a Free Agent Auction to prepare yourself for the most important event of the season',
      action: 'Mock Auction',
      onClick: () => setCreatingMock('Auction'),
    },
  ];

  // Handle not logged in state
  if (isLoadComplete && (!user || !user.id)) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2, p: 4 }}>
        <SportsFootballIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          You're not logged in
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
          Please log in to view your leagues.
        </Typography>
      </Box>
    );
  }
  
  const minTeams = 4;
    const maxTeams = 32;
    const numberOfTeamsOptions = Array.from(
      {length: maxTeams + 1 - minTeams}, 
        (_, i) => i + minTeams
    ).filter((number) => number % 2 === 0);

  return (
    <Box sx={{ 
      pb: 6,
      pt: { xs: '64px', sm: '80px' }, 
      minHeight: '100vh',
      boxSizing: 'border-box'
    }}>
      <Dialog open={!!creatingMock} maxWidth="sm" fullWidth>
        <DialogTitle>Create Mock {creatingMock}?</DialogTitle>
        <Box position="absolute" top={0} right={0}>
          <IconButton onClick={() => setCreatingMock(undefined)}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <InputLabel id="numberOfTeamsLabel">Number of Teams</InputLabel>
          <Select name="numberOfTeams" label="Number of Teams" labelId="numberOfTeamsLabel" value={numberOfTeams}
              onChange={event => setNumberOfTeams(event.target.value)}>
            {
              Object.entries(numberOfTeamsOptions).map(([key, value]) => {
                return <MenuItem key={key} value={value}>{value}</MenuItem>
              })
            }
          </Select>
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={() => setCreatingMock(undefined)}>
            Cancel
          </Button>
          <Button color="secondary" variant="contained" disabled={!numberOfTeams} onClick={createMockAuction}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          pb: 6,
          mb: 6,
          mt: { xs: '-64px', sm: '-80px' }, 
          pt: { xs: 'calc(64px + 2rem)', sm: 'calc(80px + 2rem)' } 
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="h1"
            variant={isMobile ? 'h3' : 'h2'}
            align="center"
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Welcome to CAPSized
          </Typography>
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            align="center"
            color="inherit"
            paragraph
            sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}
          >
            Your home for contract-based fantasy football
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Featured Content */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ color: 'white' }}>
            Featured
          </Typography>
          <Grid container spacing={3}>
            {featuredContent.map((item) => {
              const isExpanded = expanded === item.id;
              return (
                <Grid item xs={12} md={6} key={item.id}>
                  <FeatureCard>
                    <CardContent>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 2,
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 }
                        }}
                        onClick={(e) => handleExpandClick(item.id)(e, !isExpanded)}
                      >
                        {item.icon}
                        <Typography variant="h6" component="h3" sx={{ ml: 2, flexGrow: 1 }}>
                          {item.title}
                        </Typography>
                        <Button 
                          size="small" 
                          color="primary"
                          endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandClick(item.id)(e, !isExpanded);
                          }}
                        >
                          {isExpanded ? 'Hide' : 'Show'}
                        </Button>
                      </Box>
                      
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
                          {item.description}
                        </Typography>
                        <Button 
                          size="small" 
                          color="primary"
                          endIcon={<ArrowForwardIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            item.onClick();
                          }}
                          sx={{ mt: 1 }}
                        >
                          {item.action}
                        </Button>
                      </Collapse>
                    </CardContent>
                  </FeatureCard>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* League Showcase */}
        {user?.id && (
          <Box sx={{ mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, color: 'white' }}>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                My Leagues
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/createLeague')}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'New' : 'Create League'}
              </Button>
            </Box>
            
            <LeagueItems 
              leagues={leagues} 
              isLoadComplete={isLoadComplete} 
              isException={isException} 
              user={user} 
              isMobile={isMobile}
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Home;
